<template>
  <v-main class="modern-chat">
    <v-container fluid class="pa-0 chat-container">
      <v-row no-gutters class="chat-layout">
        <!-- Enhanced Chat List Sidebar -->
        <v-col cols="12" md="4" lg="3" class="chat-sidebar">
          <!-- Modern Header with Gradient -->
          <div class="sidebar-header">
            <div class="header-top">
              <div class="user-profile">
                <v-avatar size="44" class="user-avatar" :color="getUserAvatarColor(user?.id || 1)">
                  <v-img v-if="user?.image_path" :src="user.image_path"></v-img>
                  <span v-else class="avatar-text">
                    {{ (user?.first_name?.charAt(0) || 'U').toUpperCase() }}
                  </span>
                </v-avatar>
                <div class="user-info">
                  <h3 class="user-name">{{ user?.first_name || 'User' }}</h3>
                  <p class="user-status">
                    <v-icon small color="success" class="mr-1">mdi-circle</v-icon>
                    Available
                  </p>
                </div>
              </div>

              <div class="header-actions">
                <v-btn
                  icon
                  class="action-btn"
                  @click="showNewChatDialog = true"
                  title="Start new chat"
                >
                  <v-icon size="22">mdi-plus-circle</v-icon>
                </v-btn>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon class="action-btn" v-bind="attrs" v-on="on">
                      <v-icon size="22">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="loadChats">
                      <v-list-item-icon><v-icon small>mdi-refresh</v-icon></v-list-item-icon>
                      <v-list-item-title>Refresh</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>

            <!-- Enhanced Search Bar -->
            <div class="search-container">
              <v-text-field
                v-model="searchQuery"
                placeholder="Search conversations..."
                prepend-inner-icon="mdi-magnify"
                solo
                flat
                dense
                hide-details
                class="search-field"
              ></v-text-field>
            </div>
          </div>
          <!-- {{ chats }} -->
          <!-- Enhanced Chat List -->
          <div class="chat-list-container">
            <div v-if="chats.length === 0" class="empty-chats text-center pa-6">
              <div class="empty-icon">
                <v-icon size="80" color="primary" class="mb-3">mdi-chat-plus-outline</v-icon>
              </div>
              <h3 class="empty-title">No conversations yet</h3>
              <p class="empty-text">Start your first conversation by clicking the + button above</p>
              <v-btn
                color="primary"
                rounded
                @click="showNewChatDialog = true"
                class="mt-3"
              >
                <v-icon left>mdi-plus</v-icon>
                Start Chat
              </v-btn>
            </div>

            <div v-else class="chat-items">
              <div
                v-for="chat in filteredChats"
                :key="chat.id"
                @click="selectChat(chat)"
                :class="{
                  'active-chat': selectedChat && selectedChat.id === chat.id,
                }"
                class="modern-chat-item"
              >
                <div class="chat-avatar-container">
                  <v-avatar
                    size="56"
                    class="chat-avatar elevation-2"
                    :color="getUserAvatarColor(getOtherUser(chat)?.id)"
                  >
                    <v-img
                      v-if="getOtherUser(chat)?.image_path"
                      :src="getOtherUser(chat)?.image_path"
                    ></v-img>
                    <span v-else class="avatar-text white--text">
                      {{
                        getOtherUser(chat)?.first_name?.charAt(0).toUpperCase() ||
                        "?"
                      }}
                    </span>
                  </v-avatar>

                  <!-- Online Status Indicator -->
                  <div class="online-status" :class="{ 'online': isUserOnline(getOtherUser(chat)?.id) }">
                    <div class="status-dot"></div>
                  </div>
                </div>

                <div class="chat-content">
                  <div class="chat-header">
                    <h4 class="chat-name">
                      {{
                        getOtherUser(chat)
                          ? getOtherUser(chat).first_name +
                            " " +
                            getOtherUser(chat).last_name
                          : "Unknown"
                      }}
                    </h4>
                    <div class="chat-meta">
                      <span class="message-time">
                        {{
                          formatChatTime(
                            chat.messages?.length
                              ? chat.messages[chat.messages.length - 1].created_at
                              : chat.last_message?.created_at
                          )
                        }}
                      </span>
                      <div
                        v-if="chat.unread_count > 0"
                        class="unread-badge"
                      >
                        {{ chat.unread_count > 99 ? '99+' : chat.unread_count }}
                      </div>
                    </div>
                  </div>

                  <div class="last-message">
                    <span v-if="chat.messages?.length > 0 || chat.last_message">
                      {{ getLastMessagePreview(chat) }}
                    </span>
                    <span v-else class="no-messages">Tap to start conversation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Enhanced Chat Conversation Area -->
        <v-col cols="12" md="8" lg="9" class="chat-conversation">
          <div v-if="!selectedChat" class="no-chat-selected">
            <div class="welcome-screen">
              <div class="welcome-content">
                <div class="welcome-icon">
                  <v-icon size="120" color="primary">mdi-chat-processing</v-icon>
                </div>
                <h2 class="welcome-title">Welcome to Messages</h2>
                <p class="welcome-subtitle">
                  Select a conversation from the sidebar or start a new chat to begin messaging
                </p>
                <v-btn
                  color="primary"
                  rounded
                  large
                  @click="showNewChatDialog = true"
                  class="welcome-cta"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Start New Conversation
                </v-btn>
              </div>
            </div>
          </div>

          <div v-else class="chat-area">
            <!-- Enhanced Chat Header -->
            <div class="modern-chat-header">
              <div class="header-left">
                <div class="header-avatar-container">
                  <v-avatar
                    :color="getUserAvatarColor(getOtherUser(selectedChat)?.id)"
                    size="48"
                    class="header-avatar elevation-1"
                  >
                    <v-img
                      v-if="getOtherUser(selectedChat)?.image_path"
                      :src="getOtherUser(selectedChat)?.image_path"
                    ></v-img>
                    <span class="avatar-text-header">
                      {{
                        getOtherUser(selectedChat)
                          ?.first_name?.charAt(0)
                          .toUpperCase() || "?"
                      }}
                    </span>
                  </v-avatar>
                  <div class="header-online-status" :class="{ 'online': isUserOnline(getOtherUser(selectedChat)?.id) }">
                    <div class="status-dot"></div>
                  </div>
                </div>

                <div class="header-info">
                  <h3 class="header-name">
                    {{
                      getOtherUser(selectedChat)
                        ? getOtherUser(selectedChat).first_name +
                          " " +
                          getOtherUser(selectedChat).last_name
                        : "Unknown"
                    }}
                  </h3>
                  <p class="header-status">
                    <v-icon v-if="isUserOnline(getOtherUser(selectedChat)?.id)" small color="success" class="mr-1">
                      mdi-circle
                    </v-icon>
                    {{ isUserOnline(getOtherUser(selectedChat)?.id) ? 'Active now' : 'Last seen recently' }}
                  </p>
                </div>
              </div>

              <!-- <div class="header-actions">
                <v-btn icon class="header-action-btn" title="More options">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </div> -->
            </div>

            <!-- Messages Area -->
            <div
              ref="messagesContainer"
              class="messages-container"
              @scroll="onScroll"
            >
              <div
                class="messages-list"
                v-if="selectedChat.messages && selectedChat.messages.length > 0"
              >
                <!-- Agar selectedChat ke messages hai toh unko loop karo, warna empty array -->
                <div
                  v-for="message in selectedChat.messages"
                  :key="message.id"
                  :class="[
                    'message-wrapper',
                    message.sender_id === userId
                      ? 'message-sent'
                      : 'message-received',
                  ]"
                >
                  <div class="message-bubble">
                    <!-- Text message -->
                    <div v-if="message.message && !message.file_path" class="message-content">
                      {{ message.message }}
                    </div>

                    <!-- File message with optional text -->
                    <div v-if="message.file_path" class="message-file-container">
                      <!-- Text content if present -->
                      <div v-if="message.message && message.message !== 'File attachment'" class="message-content mb-2">
                        {{ message.message }}
                      </div>

                      <!-- Inline image display -->
                      <div v-if="isImageFile(message.file_path)" class="message-image">
                        <img
                          :src="getFileUrl(message.file_path)"
                          :alt="getFileName(message.file_path)"
                          class="chat-image"
                          @click="downloadFile(message.file_path, getFileName(message.file_path))"
                        />
                      </div>

                      <!-- Non-image file attachment -->
                      <div v-else class="message-file" @click="downloadFile(message.file_path, getFileName(message.file_path))">
                        <v-icon :color="getFileColorByPath(message.file_path)" class="mr-2">
                          {{ getFileIconByPath(message.file_path) }}
                        </v-icon>
                        <div class="file-info">
                          <div class="file-name">{{ getFileName(message.file_path) }}</div>
                          <div class="file-size-label">Click to download</div>
                        </div>
                        <v-icon small color="grey">mdi-download</v-icon>
                      </div>
                    </div>

                    <!-- Message meta -->
                    <div class="message-meta">
                      <span class="message-time">
                        {{ formatTime(message.created_at) }}
                      </span>
                      <v-icon
                        v-if="message.sender_id === userId"
                        small
                        :color="getStatusColor(message)"
                        class="ml-1"
                      >
                        {{ getStatusIcon(message) }}
                      </v-icon>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Scroll to bottom button -->
              <v-btn
                v-if="showScrollToBottom"
                fab
                small
                color="primary"
                class="scroll-to-bottom-btn"
                @click="scrollToBottom(true)"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </div>

            <!-- Message Input -->
            <div class="message-input-area">
              <div class="input-container">
                <!-- <v-btn icon @click="showEmojiPicker = !showEmojiPicker">
                  <v-icon color="grey">mdi-emoticon-happy-outline</v-icon>
                </v-btn> -->

                <v-text-field
                  v-model="newMessage"
                  placeholder="Type a message..."
                  outlined
                  dense
                  hide-details
                  class="message-input"
                  @keyup.enter="sendMessage"
                  @input="onTyping"
                ></v-text-field>

                <!-- File Input (Hidden) -->
                <input
                  ref="fileInput"
                  type="file"
                  @change="handleFileSelect"
                  style="display: none"
                  accept="*/*"
                  multiple
                >

                <v-btn
                  icon
                  @click="$refs.fileInput.click()"
                  :loading="uploadingFile"
                  title="Attach file"
                >
                  <v-icon color="grey">mdi-attachment</v-icon>
                </v-btn>

                <v-btn
                  icon
                  @click="sendMessage"
                  :color="newMessage.trim() || selectedFiles.length > 0 ? 'primary' : 'grey'"
                  :loading="sendingMessage"
                >
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>

              <!-- File Preview Area -->
              <div v-if="selectedFiles.length > 0" class="file-preview-area">
                <div class="file-preview-header">
                  <span class="preview-title">Files to send:</span>
                  <v-btn icon small @click="clearSelectedFiles">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div class="file-previews">
                  <div
                    v-for="(file, index) in selectedFiles"
                    :key="index"
                    class="file-preview-item"
                  >
                    <v-icon class="file-icon" :color="getFileColor(file.type)">
                      {{ getFileIcon(file.type) }}
                    </v-icon>
                    <div class="file-info">
                      <div class="file-name">{{ file.name }}</div>
                      <div class="file-size">{{ formatFileSize(file.size) }}</div>
                    </div>
                    <v-btn icon small @click="removeFile(index)">
                      <v-icon small color="grey">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- Typing Indicator -->
              <div v-if="isTyping" class="typing-indicator">
                <span class="typing-text"
                  >{{ getOtherUser(selectedChat)?.first_name || 'User' }} is typing</span
                >
                <div class="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- New Chat Dialog -->
    <v-dialog v-model="showNewChatDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h5">
          <v-icon class="mr-3">mdi-chat-plus</v-icon>
          Start New Chat
        </v-card-title>

        <v-card-text>
          <div class="mb-3">Select a user to start chatting with:</div>

          <!-- Search users -->
          <v-text-field
            v-model="userSearchQuery"
            placeholder="Search users..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            class="mb-4"
          ></v-text-field>

          <!-- Users List -->
          <v-list v-if="filteredAvailableUsers.length > 0" max-height="300" style="overflow-y: auto">
            <v-list-item
              v-for="user in filteredAvailableUsers"
              :key="user.id"
              @click="selectUserForChat(user)"
              class="user-item"
            >
              <v-list-item-avatar size="40">
                <v-avatar :color="'blue'" size="40">
                  <v-img v-if="user.image_path" :src="user.image_path"></v-img>
                  <span v-else class="white--text font-weight-bold">
                    {{ (user.first_name?.charAt(0) || '?').toUpperCase() }}
                  </span>
                </v-avatar>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ user.first_name }} {{ user.last_name }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="user.phone">
                  {{ user.phone }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn icon small color="primary">
                  <v-icon>mdi-chat</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <div v-else-if="availableUsers.length === 0" class="text-center py-4">
            <v-progress-circular v-if="loadingUsers" indeterminate color="primary"></v-progress-circular>
            <div v-else class="text--secondary">No users available to chat with</div>
          </div>

          <div v-else class="text-center py-4 text--secondary">
            No users found matching your search
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeNewChatDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import Echo from "@/plugins/echo";
import axios from "axios";

export default {
  name: "PersonalChats",
  data() {
    return {
      // User and chat data
      userId: null, // Mock user ID - replace with actual user from store
      searchQuery: "",
      selectedChat: null,
      newMessage: "",
      isTyping: false,
      typingTimer: null,

      // UI states
      // showChatInfo: false,
      showEmojiPicker: false,

      // New chat dialog
      showNewChatDialog: false,
      availableUsers: [],
      userSearchQuery: "",
      loadingUsers: false,

      // Online status tracking
      onlineUsers: new Set(),
      lastActivityTime: Date.now(),
      heartbeatInterval: null,
      onlineCheckInterval: null,

      // File upload
      selectedFiles: [],
      uploadingFile: false,
      sendingMessage: false,

      // Scroll options
      autoScroll: true,
      showScrollToBottom: false,
      isNearBottom: true,

      // Mock chat data - replace with API calls
      chats: [],
      // Mock messages for selected chat
      chatMessages: {}, // har chatId ke messages yaha store honge
    };
  },

  computed: {
    user() {
      return this.$store.getters['auth/user'];
    },
    filteredChats() {
      if (!this.searchQuery) return this.chats;

      return this.chats.filter(
        (chat) =>
          chat.name?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          this.getOtherUser(chat)?.first_name?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          this.getOtherUser(chat)?.last_name?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          chat.last_message?.message
            ?.toLowerCase()
            .includes(this.searchQuery.toLowerCase())
      );
    },
    filteredAvailableUsers() {
      if (!this.userSearchQuery) return this.availableUsers;

      return this.availableUsers.filter(user =>
        user.first_name?.toLowerCase().includes(this.userSearchQuery.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(this.userSearchQuery.toLowerCase()) ||
        user.phone?.includes(this.userSearchQuery)
      );
    },
  },

  watch: {
    showNewChatDialog(newVal) {
      if (newVal && this.availableUsers.length === 0) {
        this.loadAvailableUsers();
      }
    }
  },

  mounted() {
    this.initializeChat();
    this.setupEchoListeners();
    this.initializeOnlineTracking();
  },

  beforeDestroy() {
    if (this.typingTimer) clearTimeout(this.typingTimer);
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (this.onlineCheckInterval) clearInterval(this.onlineCheckInterval);
  },

  methods: {
    initializeChat() {
      // Initialize with user data
      if (this.user) {
        this.userId = this.user.id;
      }

      // Auto-select first chat if available
      if (this.chats.length > 0) {
        this.selectChat(this.chats[0]);
      }

      // Load initial chat data
      this.loadChats();
    },

    setupEchoListeners() {
      if (!this.userId || !Echo) return;

      try {
        Echo.private(`chat.${this.userId}`)
          .listen(".MessageSent", (e) => {
            this.handleNewMessage(e.message);
          })
          .listen(".MessageStatusUpdated", (e) => {
            this.handleStatusUpdate(e.chatId, e.status);
          })
          .listen(".UserTyping", (e) => {
            this.handleRemoteTyping(e.chatId, e.userId);
          })
          .error((error) => {
            console.error("Echo error:", error);
          });
      } catch (error) {
        console.error("Failed to setup Echo listeners:", error);
      }
    },

    async loadChats() {
      try {
        // Replace with actual API call
        const response = await axios.get("/chats");
        this.chats = response.data.data;
        console.log("Chats loaded");
      } catch (error) {
        console.error("Failed to load chats:", error);
      }
    },

    selectChat(chat) {
      this.selectedChat = chat;
      console.log("Selected chat:", chat);
      this.loadChatMessages(chat.id);
      this.markAsRead(chat.id);

      // Scroll to bottom of messages
      this.scrollToBottom();
    },

    async loadChatMessages(chatId) {
      try {
        const response = await axios.get(`/chats/${chatId}/messages`);
        // Update selectedChat messages directly
        if (this.selectedChat && this.selectedChat.id === chatId) {
          this.$set(this.selectedChat, 'messages', response.data.data.messages || []);
        }
        // Also store in chatMessages for reference
        this.$set(this.chatMessages, chatId, response.data.data.messages || []);
      } catch (error) {
        console.error('Failed to load chat messages:', error);
      }
    },
    async sendMessage() {
      if ((!this.newMessage.trim() && this.selectedFiles.length === 0) || !this.selectedChat) return;
      console.log("Preparing to send message...", this.selectedChat);

      // Get logged-in user ID
      const myId = this.user.id;

      // Find the receiver (the other user in this chat)
      const receiver = this.selectedChat.users.find((u) => u.id !== myId);
      console.log("Receiver found:", receiver);

      if (!receiver) {
        console.error("Receiver not found in chat");
        return;
      }

      this.sendingMessage = true;

      try {
        // If files are selected, send as FormData
        if (this.selectedFiles.length > 0) {
          for (let file of this.selectedFiles) {
            const formData = new FormData();
            formData.append('user_id', receiver.id);
            formData.append('message', this.newMessage.trim() || 'File attachment');
            formData.append('file', file);

            const res = await axios.post("/chat-messages", formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });

            // Update local chat data with the response
            if (res.data.success && res.data.data) {
              this.selectedChat.messages = res.data.data.messages || [];
            }
          }

          // Clear files after sending
          this.clearSelectedFiles();
        } else {
          // Send text message only
          const res = await axios.post("/chat-messages", {
            user_id: receiver.id,
            message: this.newMessage.trim(),
          });

          // Update local chat data with the response
          if (res.data.success && res.data.data) {
            this.selectedChat.messages = res.data.data.messages || [];
          }
        }

        this.newMessage = "";

        // Scroll to bottom after sending message
        this.$nextTick(() => {
          this.scrollToBottom(true);
        });
      } catch (error) {
        console.error("Failed to send message:", error);
        alert("Failed to send message. Please try again.");
      } finally {
        this.sendingMessage = false;
      }
    },
    handleNewMessage(message) {
      // Find the chat that this message belongs to
      const chatId = message.chat_id;
      const chat = this.chats.find(c => c.id === chatId);

      if (chat) {
        // Add message to the chat
        if (!chat.messages) {
          this.$set(chat, 'messages', []);
        }
        chat.messages.push(message);

        // If this is the currently selected chat, scroll to bottom
        if (this.selectedChat && this.selectedChat.id === chatId) {
          this.$nextTick(() => {
            this.scrollToBottom(true);
          });
        }

        // Update last message info in chat list
        this.$set(chat, 'last_message', message);

        // Update unread count if not the sender
        if (message.sender_id !== this.userId) {
          const currentUnread = chat.unread_count || 0;
          this.$set(chat, 'unread_count', currentUnread + 1);
        }
      }
    },

    onTyping() {
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      } else if (this.selectedChat) {
        // Send typing indicator on first keystroke
        axios.post('/chats/typing', { chat_id: this.selectedChat.id }).catch(() => {});
      }

      this.typingTimer = setTimeout(() => {
        this.typingTimer = null;
      }, 3000);
    },

    onScroll() {
      const container = this.$refs.messagesContainer;
      if (!container) return;

      // Check if user is near bottom (within 100px)
      const scrollPosition = container.scrollTop + container.clientHeight;
      const scrollHeight = container.scrollHeight;
      this.isNearBottom = scrollPosition >= scrollHeight - 100;

      // Show/hide scroll to bottom button
      this.showScrollToBottom = !this.isNearBottom;

      // Implement infinite scroll for message history
      if (container.scrollTop === 0) {
        this.loadMoreMessages();
      }
    },

    loadMoreMessages() {
      // Implement pagination for messages
      console.log("Loading more messages...");
    },

    async markAsRead(chatId) {
      const chat = this.chats.find(c => c.id === chatId);
      if (chat && chat.unread_count > 0) {
        this.$set(chat, 'unread_count', 0);

        try {
          await axios.post(`/chats/${chatId}/mark-read`);
          // Update local message statuses
          if (chat.messages) {
            chat.messages.forEach(m => {
              if (m.sender_id !== this.userId) {
                m.status = 'read';
                m.seen = true;
              }
            });
          }
        } catch (error) {
          console.error('Failed to mark messages as read:', error);
        }
      }
    },

    scrollToBottom(forceScroll = false) {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container && (this.autoScroll || forceScroll)) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
          });
          // Update state after scroll
          setTimeout(() => {
            this.isNearBottom = true;
            this.showScrollToBottom = false;
          }, 300);
        }
      });
    },

    scrollToTop() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      });
    },

    toggleAutoScroll() {
      this.autoScroll = !this.autoScroll;
      if (this.autoScroll) {
        this.scrollToBottom(true);
      }
    },

    handleFileUpload(event) {
      const files = event.target.files;
      for (let file of files) {
        console.log("Uploading file:", file.name);
        // Implement file upload logic
      }
    },
    getOtherUser(chat) {
      if (!chat || !chat.users) return null;
      return (
        chat.users.find((u) => u.id !== this.userId) || chat.users[0] || null
      );
    },

    getLastMessagePreview(chat) {
      let lastMessage = null;

      if (chat.messages?.length > 0) {
        lastMessage = chat.messages[chat.messages.length - 1];
      } else if (chat.last_message) {
        lastMessage = chat.last_message;
      }

      if (!lastMessage) return "";

      const isMe = lastMessage.sender_id === this.userId;
      const prefix = isMe ? "You: " : "";
      const message = lastMessage.message || "📄 File";

      return prefix + (message.length > 30 ? message.substring(0, 30) + "..." : message);
    },

    formatTime(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatChatTime(date) {
      if (!date) return "";
      const d = new Date(date);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "now";
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;

      return d.toLocaleDateString();
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    async loadAvailableUsers() {
      this.loadingUsers = true;
      try {
        console.log('Loading available users...');
        const response = await axios.get('/chats/available-users');
        console.log('Available users response:', response.data);

        if (response.data && response.data.success) {
          this.availableUsers = response.data.data || [];
        } else {
          console.error('API returned unsuccessful response:', response.data);
          this.availableUsers = [];
        }
      } catch (error) {
        console.error('Failed to load available users:', error);
        if (error.response) {
          console.error('Error response:', error.response.data);
          console.error('Error status:', error.response.status);
        }
        alert('Failed to load users. Please check your connection and try again.');
      } finally {
        this.loadingUsers = false;
      }
    },

    async selectUserForChat(user) {
      try {
        this.loadingUsers = true;

        // Create or get existing chat with this user
        const response = await axios.post('/chats', {
          user_id: user.id,
          is_group: false
        });

        if (response.data.success) {
          const newChat = response.data.data;

          // Check if this chat already exists in our local chats
          const existingChatIndex = this.chats.findIndex(c => c.id === newChat.id);

          if (existingChatIndex === -1) {
            // Add new chat to the list
            this.chats.unshift(newChat);
          } else {
            // Update existing chat
            this.$set(this.chats, existingChatIndex, newChat);
          }

          // Select this chat
          this.selectChat(newChat);

          // Close dialog
          this.closeNewChatDialog();

          // Show success message
          console.log(`Started chat with ${user.first_name} ${user.last_name}`);
        }
      } catch (error) {
        console.error('Failed to create chat:', error);
        alert('Failed to start chat. Please try again.');
      } finally {
        this.loadingUsers = false;
      }
    },

    closeNewChatDialog() {
      this.showNewChatDialog = false;
      this.userSearchQuery = "";
    },

    getUserAvatarColor(userId) {
      if (!userId) return '#6C7B7F';

      // Array of modern, attractive colors
      const colors = [
        '#FF6B6B', // Coral Red
        '#4ECDC4', // Teal
        '#45B7D1', // Sky Blue
        '#96CEB4', // Mint Green
        '#FFEAA7', // Light Yellow
        '#DDA0DD', // Plum
        '#FFB347', // Peach
        '#87CEEB', // Sky Blue Light
        '#98D8C8', // Aquamarine
        '#F7DC6F', // Light Gold
        '#BB8FCE', // Light Purple
        '#85C1E9', // Light Blue
        '#F8C471', // Light Orange
        '#82E0AA', // Light Green
        '#F1948A', // Light Red
      ];

      // Use user ID to consistently assign same color
      const colorIndex = userId % colors.length;
      return colors[colorIndex];
    },

    isUserOnline(userId) {
      if (!userId) return false;

      // Current logged-in user is always online
      if (this.userId && userId === this.userId) {
        return true;
      }

      // Check if user has recent activity (within last 5 minutes)
      // In production, this would come from your real-time presence system
      const now = Date.now();
      const fiveMinutesAgo = now - (5 * 60 * 1000);

      // For now, simulate checking last_seen timestamp from user data
      // You would replace this with actual API call or Socket.io presence data
      const user = this.chats
        .flatMap(chat => chat.users || [])
        .find(u => u.id === userId);

      if (user && user.last_seen) {
        const lastSeen = new Date(user.last_seen).getTime();
        return lastSeen > fiveMinutesAgo;
      }

      // Fallback: check our online users set
      return this.onlineUsers.has(userId);
    },

    // Update online status dynamically
    updateUserOnlineStatus(userId, isOnline) {
      if (isOnline) {
        this.onlineUsers.add(userId);
      } else {
        this.onlineUsers.delete(userId);
      }
    },

    initializeOnlineTracking() {
      if (this.userId) {
        this.updateUserOnlineStatus(this.userId, true);
      }

      // Send heartbeat every 60 seconds
      this.sendHeartbeat();
      this.heartbeatInterval = setInterval(() => {
        this.sendHeartbeat();
      }, 60000);

      // Check online status every 30 seconds
      this.onlineCheckInterval = setInterval(() => {
        this.checkUsersOnlineStatus();
      }, 30000);
    },

    async checkUsersOnlineStatus() {
      try {
        const allUserIds = this.chats
          .flatMap(chat => chat.users || [])
          .map(user => user.id)
          .filter(id => id !== this.userId);

        if (allUserIds.length === 0) return;

        const response = await axios.post('/chats/online-status', {
          user_ids: [...new Set(allUserIds)]
        });

        if (response.data.success) {
          const onlineIds = response.data.data;
          // Reset all, then set online ones
          this.onlineUsers = new Set(onlineIds);
          if (this.userId) this.onlineUsers.add(this.userId);
          // Force reactivity
          this.$forceUpdate();
        }
      } catch (error) {
        console.error('Failed to check online status:', error);
      }
    },

    async sendHeartbeat() {
      try {
        await axios.post('/chats/heartbeat');
      } catch (error) {
        // Silently fail for heartbeat
      }
    },

    handleRemoteTyping(chatId) {
      if (this.selectedChat && this.selectedChat.id === chatId) {
        this.isTyping = true;
        // Clear typing after 3 seconds
        setTimeout(() => {
          this.isTyping = false;
        }, 3000);
      }
    },

    handleStatusUpdate(chatId, status) {
      const chat = this.chats.find(c => c.id === chatId);
      if (chat && chat.messages) {
        chat.messages.forEach(m => {
          if (m.sender_id === this.userId) {
            m.status = status;
            if (status === 'read') m.seen = true;
          }
        });
      }
    },

    getStatusIcon(message) {
      const status = message.status || (message.seen ? 'read' : 'sent');
      if (status === 'read') return 'mdi-check-all';
      if (status === 'delivered') return 'mdi-check-all';
      return 'mdi-check';
    },

    getStatusColor(message) {
      const status = message.status || (message.seen ? 'read' : 'sent');
      if (status === 'read') return 'blue';
      return 'grey';
    },

    isImageFile(filePath) {
      if (!filePath) return false;
      const ext = filePath.toLowerCase();
      return ext.includes('.jpg') || ext.includes('.jpeg') || ext.includes('.png') ||
             ext.includes('.gif') || ext.includes('.webp') || ext.includes('.bmp') ||
             ext.includes('.svg');
    },

    getFileUrl(filePath) {
      if (!filePath) return '';
      return `${process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000'}/storage/${filePath}`;
    },

    // File handling methods
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        this.selectedFiles = [...this.selectedFiles, ...files];
        console.log('Files selected:', this.selectedFiles);
      }
    },

    clearSelectedFiles() {
      this.selectedFiles = [];
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },

    getFileIcon(fileType) {
      if (fileType.startsWith('image/')) return 'mdi-image';
      if (fileType.startsWith('video/')) return 'mdi-video';
      if (fileType.startsWith('audio/')) return 'mdi-music';
      if (fileType.includes('pdf')) return 'mdi-file-pdf-box';
      if (fileType.includes('word')) return 'mdi-file-word-box';
      if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'mdi-file-excel-box';
      if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'mdi-file-powerpoint-box';
      if (fileType.includes('zip') || fileType.includes('rar')) return 'mdi-folder-zip';
      if (fileType.includes('text')) return 'mdi-file-document-outline';
      return 'mdi-file-document';
    },

    getFileColor(fileType) {
      if (fileType.startsWith('image/')) return 'green';
      if (fileType.startsWith('video/')) return 'red';
      if (fileType.startsWith('audio/')) return 'purple';
      if (fileType.includes('pdf')) return 'red';
      if (fileType.includes('word')) return 'blue';
      if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'green';
      if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'orange';
      if (fileType.includes('zip') || fileType.includes('rar')) return 'yellow';
      return 'grey';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // File path utilities
    getFileName(filePath) {
      if (!filePath) return 'Unknown file';
      return filePath.split('/').pop() || 'Unknown file';
    },

    getFileIconByPath(filePath) {
      if (!filePath) return 'mdi-file-document';
      const fileName = filePath.toLowerCase();

      if (fileName.includes('.jpg') || fileName.includes('.jpeg') || fileName.includes('.png') || fileName.includes('.gif') || fileName.includes('.webp')) {
        return 'mdi-image';
      }
      if (fileName.includes('.mp4') || fileName.includes('.avi') || fileName.includes('.mov') || fileName.includes('.mkv')) {
        return 'mdi-video';
      }
      if (fileName.includes('.mp3') || fileName.includes('.wav') || fileName.includes('.flac') || fileName.includes('.aac')) {
        return 'mdi-music';
      }
      if (fileName.includes('.pdf')) return 'mdi-file-pdf-box';
      if (fileName.includes('.doc') || fileName.includes('.docx')) return 'mdi-file-word-box';
      if (fileName.includes('.xls') || fileName.includes('.xlsx')) return 'mdi-file-excel-box';
      if (fileName.includes('.ppt') || fileName.includes('.pptx')) return 'mdi-file-powerpoint-box';
      if (fileName.includes('.zip') || fileName.includes('.rar') || fileName.includes('.7z')) return 'mdi-folder-zip';
      if (fileName.includes('.txt') || fileName.includes('.md')) return 'mdi-file-document-outline';
      return 'mdi-file-document';
    },

    getFileColorByPath(filePath) {
      if (!filePath) return 'grey';
      const fileName = filePath.toLowerCase();

      if (fileName.includes('.jpg') || fileName.includes('.jpeg') || fileName.includes('.png') || fileName.includes('.gif') || fileName.includes('.webp')) {
        return 'green';
      }
      if (fileName.includes('.mp4') || fileName.includes('.avi') || fileName.includes('.mov') || fileName.includes('.mkv')) {
        return 'red';
      }
      if (fileName.includes('.mp3') || fileName.includes('.wav') || fileName.includes('.flac') || fileName.includes('.aac')) {
        return 'purple';
      }
      if (fileName.includes('.pdf')) return 'red';
      if (fileName.includes('.doc') || fileName.includes('.docx')) return 'blue';
      if (fileName.includes('.xls') || fileName.includes('.xlsx')) return 'green';
      if (fileName.includes('.ppt') || fileName.includes('.pptx')) return 'orange';
      if (fileName.includes('.zip') || fileName.includes('.rar') || fileName.includes('.7z')) return 'yellow';
      return 'grey';
    },

    async downloadFile(filePath, fileName) {
      try {
        // In production, this would use the proper file URL from your storage
        const fileUrl = `${process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000'}/storage/${filePath}`;

        // Create a temporary link element to download file
        const link = document.createElement('a');
        link.href = fileUrl;
        link.target = '_blank';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Download failed:', error);
        alert('Failed to download file. Please try again.');
      }
    },
  },
};
</script>

<style scoped>
.modern-chat {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
}

.chat-container {
  height: calc(100vh - 64px);
  background: transparent;
  overflow: hidden;
}

.chat-layout {
  height: 100%;
  overflow: hidden;
}

/* Enhanced Chat Sidebar */
.chat-sidebar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.37);
  border-right: 1px solid rgba(255, 255, 255, 0.18);
}

.sidebar-header {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0;
  flex-shrink: 0;
}

.header-top {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.avatar-text {
  font-weight: 600;
  font-size: 1.1rem;
}

.user-info {
  flex: 1;
}

.user-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 2px 0;
}

.user-status {
  font-size: 0.85rem;
  color: #7f8c8d;
  margin: 0;
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-btn:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-1px);
}

.search-container {
  padding: 0 20px 20px 20px;
}

.search-field >>> .v-input__control {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
}

.search-field >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.9) !important;
  border-radius: 12px !important;
}

/* Enhanced Chat List */
.chat-list-container {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px;
  min-height: 0;
}

.empty-chats {
  padding: 60px 20px;
}

.empty-icon {
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.empty-text {
  color: #7f8c8d;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 0;
}

.chat-items {
  padding: 8px 0;
}

.modern-chat-item {
  display: flex;
  align-items: center;
  padding: 16px 12px;
  margin-bottom: 4px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.modern-chat-item:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.modern-chat-item.active-chat {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.modern-chat-item.active-chat .chat-name {
  color: #000000 !important;
  font-weight: 800 !important;
  text-shadow: 0 1px 3px rgba(255, 255, 255, 0.8);
}

.modern-chat-item.active-chat .message-time {
  color: rgba(255, 255, 255, 0.9);
}

.modern-chat-item.active-chat .last-message {
  color: rgba(255, 255, 255, 0.8);
}

.chat-avatar-container {
  position: relative;
  margin-right: 16px;
}

.chat-avatar {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.online-status {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #bdc3c7;
  transition: all 0.3s ease;
}

.online-status.online .status-dot {
  background: #27ae60;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(39, 174, 96, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(39, 174, 96, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(39, 174, 96, 0);
  }
}

.chat-content {
  flex: 1;
  min-width: 0;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.chat-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.chat-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.message-time {
  font-size: 0.75rem;
  color: #7f8c8d;
  font-weight: 500;
}

.unread-badge {
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(231, 76, 60, 0.3);
}

.last-message {
  font-size: 0.85rem;
  color: #7f8c8d;
  line-height: 1.3;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
}

.no-messages {
  font-style: italic;
  color: #bdc3c7;
}

/* Enhanced Chat Conversation */
.chat-conversation {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.no-chat-selected {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
}

.welcome-screen {
  text-align: center;
  padding: 40px;
  max-width: 400px;
}

.welcome-content {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.37);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.welcome-icon {
  margin-bottom: 24px;
}

.welcome-title {
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 16px;
}

.welcome-subtitle {
  color: #7f8c8d;
  font-size: 1.1rem;
  line-height: 1.5;
  margin-bottom: 32px;
}

.welcome-cta {
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  text-transform: none;
  font-weight: 600;
  padding: 12px 24px;
  font-size: 1rem;
}

.welcome-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
}

/* Enhanced Chat Header in Conversation */
.modern-chat-header {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85));
  backdrop-filter: blur(20px);
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-avatar-container {
  position: relative;
}

.header-avatar {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.avatar-text-header {
  color: white !important;
  font-weight: 700 !important;
  font-size: 1.2rem !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;
}

.header-online-status {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-online-status .status-dot {
  width: 10px;
  height: 10px;
}

.header-info {
  flex: 1;
}

.header-name {
  font-size: 1.2rem;
  font-weight: 800 !important;
  color: #111111 !important;
  margin: 0 0 4px 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.header-status {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin: 0;
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.header-action-btn {
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #7f8c8d;
}

.header-action-btn:hover {
  background: rgba(255, 255, 255, 1);
  color: #2c3e50;
  transform: translateY(-1px);
}

/* Scrollbar Styling */
.chat-list-container::-webkit-scrollbar,
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.chat-list-container::-webkit-scrollbar-track,
.messages-container::-webkit-scrollbar-track {
  background: transparent;
}

.chat-list-container::-webkit-scrollbar-thumb,
.messages-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.chat-list-container::-webkit-scrollbar-thumb:hover,
.messages-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Enhanced Mobile Responsiveness */
@media (max-width: 768px) {
  .chat-sidebar {
    position: fixed;
    z-index: 1000;
    width: 100%;
    height: 100%;
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .chat-sidebar.mobile-open {
    transform: translateX(0);
  }

  .modern-chat-header {
    padding: 16px 20px;
  }

  .header-name {
    font-size: 1.1rem;
  }

  .modern-chat-item {
    padding: 12px;
    margin-bottom: 2px;
  }

  .chat-avatar {
    width: 48px !important;
    height: 48px !important;
  }

  .welcome-content {
    padding: 30px 20px;
  }

  .welcome-title {
    font-size: 1.6rem;
  }

  .empty-title {
    font-size: 1.2rem;
  }
}

@media (max-width: 480px) {
  .header-top {
    padding: 16px;
  }

  .user-name {
    font-size: 1rem;
  }

  .modern-chat-item {
    padding: 10px;
  }

  .chat-name {
    font-size: 0.95rem;
  }

  .last-message {
    font-size: 0.8rem;
  }
}

/* Animation Enhancements */
.modern-chat-item,
.action-btn,
.header-action-btn,
.welcome-cta {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Focus States */
.action-btn:focus,
.header-action-btn:focus {
  outline: 2px solid rgba(102, 126, 234, 0.5);
  outline-offset: 2px;
}

/* Loading States */
.chat-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

/* Dark theme support */
@media (prefers-color-scheme: dark) {
  .modern-chat {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  }

  .chat-sidebar {
    background: rgba(44, 62, 80, 0.95);
  }

  .user-name,
  .chat-name,
  .header-name {
    color: #ecf0f1;
  }

  .user-status,
  .last-message,
  .header-status {
    color: #bdc3c7;
  }
}

/* Chat List */
.chat-list {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.chat-item {
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;
  cursor: pointer;
  padding: 12px 16px;
}

.chat-item:hover {
  background: #f8f9fa;
}

.chat-item.active-chat {
  background: #e3f2fd;
  border-right: 3px solid #2196f3;
}

.chat-name {
  font-weight: 500;
  font-size: 1rem;
  color: #1a1a1a;
  display: flex;
  align-items: center;
}

.last-message {
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.message-time {
  font-size: 0.75rem;
  color: #999;
  margin-bottom: 4px;
}

.online-indicator {
  margin-top: 4px;
}

/* Chat Conversation */
.chat-conversation {
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.no-chat-selected {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.chat-area {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Chat Header */
.chat-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-info {
  flex: 1;
}

.chat-header-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
}

.chat-status {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 8px;
}

/* Messages Container */
.messages-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  background: #f8f9fa;
  background-image: radial-gradient(
      circle at 25px 25px,
      rgba(255, 255, 255, 0.8) 2px,
      transparent 0
    ),
    radial-gradient(
      circle at 75px 75px,
      rgba(255, 255, 255, 0.8) 2px,
      transparent 0
    );
  background-size: 100px 100px;
  position: relative;
  min-height: 0;
}

/* Scroll to bottom button */
.scroll-to-bottom-btn {
  position: absolute !important;
  bottom: 20px;
  right: 20px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3) !important;
  transition: all 0.3s ease;
}

.scroll-to-bottom-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(33, 150, 243, 0.4) !important;
}

.messages-list {
  padding: 20px;
  min-height: 100%;
}

/* Messages */
.message-wrapper {
  margin-bottom: 12px;
  display: flex;
}

.message-wrapper.message-sent {
  justify-content: flex-end;
}

.message-wrapper.message-received {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 70%;
  min-width: 100px;
  border-radius: 16px;
  padding: 12px 16px 8px 16px;
  position: relative;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.message-sent .message-bubble {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-received .message-bubble {
  background: white;
  color: #1a1a1a;
  border-bottom-left-radius: 4px;
  border: 1px solid #e0e0e0;
}

.message-content {
  font-size: 0.95rem;
  line-height: 1.4;
  word-wrap: break-word;
}

.message-image {
  margin-bottom: 4px;
}

.chat-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  cursor: pointer;
  object-fit: cover;
  display: block;
}

.chat-image:hover {
  opacity: 0.9;
}

.message-file-container {
  width: 100%;
}

.message-file {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  margin-bottom: 4px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.message-file:hover {
  background: rgba(255, 255, 255, 0.2);
}

.message-sent .message-file {
  background: rgba(255, 255, 255, 0.2);
}

.message-sent .message-file:hover {
  background: rgba(255, 255, 255, 0.3);
}

.message-received .message-file {
  background: #f5f5f5;
}

.message-received .message-file:hover {
  background: #eee;
}

.file-info {
  flex: 1;
  margin-left: 8px;
}

.file-name {
  font-weight: 500;
  font-size: 0.85rem;
  line-height: 1.2;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size-label {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 2px;
}

/* File Preview Area */
.file-preview-area {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 12px;
  margin-top: 12px;
  border: 1px solid #e0e0e0;
}

.file-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.preview-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #666;
}

.file-previews {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-preview-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.file-icon {
  margin-right: 12px;
}

.file-preview-item .file-info {
  flex: 1;
}

.file-preview-item .file-name {
  font-size: 0.9rem;
  max-width: 180px;
}

.file-preview-item .file-size {
  font-size: 0.75rem;
  color: #666;
  margin-top: 2px;
}

.message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
  gap: 4px;
}

.message-time {
  font-size: 0.75rem;
  opacity: 0.7;
}

.message-sent .message-time {
  color: rgba(255, 255, 255, 0.8);
}

.message-received .message-time {
  color: #999;
}

/* Message Input */
.message-input-area {
  background: white;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
}

/* Scroll Options Bar */
.scroll-options-bar {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
}

.scroll-options-bar .v-btn {
  min-width: 32px !important;
  width: 32px;
  height: 32px;
}

.scroll-options-bar .v-btn .v-icon {
  font-size: 18px;
}

.input-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-input {
  flex: 1;
}

.message-input >>> .v-input__control {
  min-height: 44px;
}

.message-input >>> .v-text-field__details {
  display: none;
}

.message-input >>> .v-input__slot {
  border-radius: 20px !important;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.85rem;
  color: #666;
}

.typing-text {
  font-style: italic;
}

.typing-dots {
  display: flex;
  gap: 2px;
}

.typing-dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #666;
  animation: typing-pulse 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}
.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing-pulse {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Scrollbar Styling */
.messages-container::-webkit-scrollbar,
.chat-list::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track,
.chat-list::-webkit-scrollbar-track {
  background: transparent;
}

.messages-container::-webkit-scrollbar-thumb,
.chat-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover,
.chat-list::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

/* Dialog Styling */
.v-dialog >>> .v-card {
  border-radius: 16px !important;
}

.v-dialog >>> .v-card-title {
  font-weight: 600;
  font-size: 1.2rem;
  color: #1a1a1a;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .chat-sidebar {
    position: absolute;
    z-index: 10;
    width: 100%;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  .chat-sidebar.mobile-open {
    transform: translateX(0);
  }

  .sidebar-header {
    padding: 16px;
  }

  .chat-title {
    font-size: 1.3rem;
  }

  .messages-list {
    padding: 16px;
  }

  .message-bubble {
    max-width: 85%;
  }

  .chat-header {
    padding: 12px 16px;
  }

  .message-input-area {
    padding: 12px 16px;
  }
}

/* Animation for new messages */
.message-wrapper {
  animation: messageSlideIn 0.3s ease-out;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* New Chat Dialog Styling */
.user-item {
  transition: all 0.2s ease;
  cursor: pointer;
  border-radius: 8px;
  margin: 4px 0;
}

.user-item:hover {
  background: #f5f5f5;
}

/* Unread message indicator */
.chat-item >>> .v-chip {
  min-width: 20px;
  height: 20px;
  font-size: 0.7rem;
  font-weight: 600;
}

/* Online status styling */
.v-list-item-action .v-icon {
  font-size: 12px !important;
}

/* Input field focus styling */
.message-input >>> .v-input--is-focused .v-input__slot {
  border-color: #2196f3 !important;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

/* Button hover effects */
.v-btn:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

/* List item avatar sizing */
.v-list-item-avatar {
  margin-right: 12px !important;
}

/* Search field styling */
.search-field >>> .v-input__slot::before {
  border-color: transparent;
}

.search-field >>> .v-input__slot::after {
  border-color: transparent;
}

/* Card elevation for dialogs */
.v-card {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
}
</style>
